import { Dialog } from 'primereact/dialog'
import React, { useCallback, useContext, useState } from 'react'
import { Button } from 'primereact/button'
import { useMountEffect } from 'primereact/hooks'
import { isEmpty } from 'lodash'
import { CommonContext } from '../common/context/commonContext'
import { GoodsDetailDialogProps, GoodsDetailDialogReq, InventoryGoodsDetail } from '../../types/inventory'
import { Divider } from 'primereact/divider'
import { getImageUrl } from '../../utils/imageUtils'
import { Image } from 'primereact/image'
import InventoryGoodsAdjustmentDialog from './InventoryGoodsAdjustmentDialog'
import InventoryGoodsMoveDialog from './InventoryGoodsMoveDialog'
import { includeComma } from '../../utils/inputText'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { tokens } from 'locales/tokens'

const GoodsDetailDialog = (props: GoodsDetailDialogProps) => {
    const { $toast, $http, $alert } = useContext(CommonContext)
    const { t } = useTranslation()
    const [inventoryGoodsMoveVisible, setInventoryGoodsMoveVisible] = useState<boolean>(false)
    const [inventoryGoodsAdjustmentVisible, setInventoryGoodsAdjustmentVisible] = useState<boolean>(false)

    const [params, setParams] = useState<GoodsDetailDialogReq>({
        lctPathNm: '',
        lctSeq: '',
        goodsId: '',
        invnSeq: 0,
        lctTypCd: '',
        locationAlias: '',
    })
    const [goods, setGoods] = useState<InventoryGoodsDetail>({
        rowNum: 0,
        lctSeq: '',
        goodsId: '',
        goodsNm: '',
        centerId: '',
        centerNm: '',
        invnSeq: 0,
        goodsQty: 0,
        pckGoodsQty: 0,
        avlbGoodsQty: 0,
        gdslsUnitCd: '',
        specTxt: '',
        shlfYmd: '',
        shlfYmdFm: '',
        shlfRemainDay: 0,
        strgDay: 0,
        prchUnitAmt: 0,
        imageUri: '',
        shlfYn: '',
        lotNo: '',
        sysCd: '',
    })

    useMountEffect(() => {
        if (!props.visible) return
        if (!props.data) return
        console.log('useMountEffect=', props.data)
        setParams(props.data)
        findGoodsDetail(props.data)
    })

    const findGoodsDetail = useCallback(
        async (params: any) => {
            const result = await $http().post('/inventory/find-goods-detail', params)
            if (isEmpty(result)) {
                $toast({ title: t(tokens.common.product_inquiry), text: t(tokens.common.no_loaded_items), icon: 'success' })
                return
            }
            console.log('findGoodsDetail=', result)
            setGoods(result)
        },
        [$http],
    )

    const onClickAdjustmentOpen = () => {
        setInventoryGoodsAdjustmentVisible(true)
    }
    const onClickAdjustmentHide = (isChange: boolean) => {
        if (isChange) {
            props.onHide(true)
        }
        setInventoryGoodsAdjustmentVisible(false)
    }
    const onClickMoveOpen = () => {
        setInventoryGoodsMoveVisible(true)
    }
    const onClickMoveHide = (isChange: boolean) => {
        if (isChange) {
            props.onHide(true)
        }
        setInventoryGoodsMoveVisible(false)
    }

    const onClickSave = async () => {
        if (!props.data) return

        const isConfirm = await $alert({
            icon: 'info',
            title: t(tokens.common.add_zone),
            text: t(tokens.inventory.confirm_location_zone_addition),
            isConfirm: true,
        })

        if (!isConfirm) return

        const result = await $http().put('/inventory/add-area', {})
        if (result) {
            $toast({ title: t(tokens.common.success), text: t(tokens.inventory.zone_added), icon: 'success' })
            props.onHide(true)
        }
    }

    const header = (title: string) => {
        return (
            <>
                <div className="col-12 flex grid py-0">
                    <div>{title}</div>
                    {params.lctSeq !== null ? (
                        <div className="col-12 flex text-sm pl-0 rdms-text-deepblue"> {params.lctPathNm} [ {params.locationAlias} ]</div>
                    ) : (
                        <div className="col-12 flex text-sm pl-0 rdms-text-danger"> {t(tokens.common.not_placed)} </div>
                    )}
                </div>
                <Divider className="my-0" />
            </>
        )
    }

    const footer = () => {
        return (
            <div className="grid pt-2">
                {params.lctSeq !== null && // 로케이션 적치된 상품, (이동)
                params.lctTypCd !== 'BD' &&
                params.lctTypCd !== 'RT' &&
                params.lctTypCd !== 'TK' &&
                params.lctTypCd !== 'MV' &&
                params.lctTypCd !== 'IN' &&
                params.lctTypCd !== 'DB' ? ( // (불량, 반품, 회수, 이동, 입고, 분배) 로케이션 적치된 상품-(이동), (조정) 불가... 관리항목으로 적치된 상품이므로
                    <>
                        <div className="col-6 flex p-0">
                            <Button
                                className="w-full p-button-success p-2"
                                label={t(tokens.inventory.stock_movement)}
                                size="large"
                                onClick={onClickMoveOpen}
                            />
                        </div>
                        <div className="col-6 flex p-0">
                            <Button
                                className="w-full p-button-warning p-2"
                                label={t(tokens.inventory.inventory_adjustment)}
                                size="large"
                                onClick={onClickAdjustmentOpen}
                            />
                        </div>
                    </>
                ) : null}
            </div>
        )
    }

    return (
        <Dialog
            headerClassName="pb-3"
            header={header(t(tokens.common.product_details))}
            visible={props.visible}
            onHide={() => props.onHide(false)}
            contentClassName="pb-0"
            footer={footer}
        >
            {goods && (
                <div className="grid">
                    <div className="col-12 flex font-bold rdms-text-deepblue">{t(tokens.common.product_name)} / {t(tokens.common.product_barcode)}</div>
                    <div className="col-12 flex font-semibold mt-0 pt-0">{goods.goodsNm} / {goods.sysCd}</div>
                    <div className="col-12 flex font-bold mt-2 rdms-text-deepblue">{t(tokens.common.specification)}</div>
                    <div className="col-12 flex font-semibold mt-0 pt-0">{goods.specTxt}</div>
                    <div className="col-12 flex font-bold mt-2 rdms-text-deepblue">
                        {t(tokens.common.actual_inventory)} / {t(tokens.common.scheduled_outbound)} / {t(tokens.common.movable)}
                    </div>
                    <div className="col-12 flex font-semibold mt-0 pt-0">
                        <span>
                            {includeComma(goods.goodsQty, '-')} {goods.gdslsUnitCd}
                        </span>
                        <span className="px-3">/</span>
                        <span>
                            {goods.pckGoodsQty} {goods.gdslsUnitCd}
                        </span>
                        <span className="px-3">/</span>
                        <span>
                            {includeComma(goods.avlbGoodsQty, '-')} {goods.gdslsUnitCd}
                        </span>
                    </div>
                    <div className="col-12 flex font-bold mt-2 rdms-text-deepblue">{t(tokens.common.storage_duration)}</div>
                    <div className="col-12 flex font-semibold mt-0 pt-0">
                        {includeComma(goods.strgDay, '-')} {t(tokens.common.day)}
                    </div>
                    <div className="col-12 flex font-bold mt-2 rdms-text-deepblue">{t(tokens.common.expiration_date)} / {t(tokens.common.lot_no)}</div>
                    <div className="col-12 flex font-semibold mt-0 pt-0 rdms-text-danger">
                        {goods.shlfYmdFm} (
                        {goods.shlfRemainDay <= 0 ? 'D+' + includeComma(-goods.shlfRemainDay) : 'D-' + includeComma(goods.shlfRemainDay)}) / {goods.lotNo}
                    </div>

                    <div className="col-12 p-align-center pt-3">
                        <Image src={getImageUrl(goods.imageUri)} width="250" />
                    </div>
                </div>
            )}
            {inventoryGoodsMoveVisible && (
                <InventoryGoodsMoveDialog visible={inventoryGoodsMoveVisible} onHide={(e) => onClickMoveHide(e)} data={goods} />
            )}
            {inventoryGoodsAdjustmentVisible && (
                <InventoryGoodsAdjustmentDialog visible={inventoryGoodsAdjustmentVisible} onHide={(e) => onClickAdjustmentHide(e)} data={goods} />
            )}
        </Dialog>
    )
}

export default GoodsDetailDialog
